import { useState } from "react";
import { SupportLayout } from "@/components/support-layout";
import {
  BookOpen,
  Activity,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  ExternalLink,
  ArrowRight,
} from "lucide-react";

const API_URL =
  import.meta.env.VITE_BACKEND_API_URL || "https://api.nordex.trade";

const DOCS_URL = import.meta.env.VITE_DOCS_URL || "/docs/";
const STATUS_URL =
  import.meta.env.VITE_STATUS_URL || "https://status.nordex.trade";

const GOLD = "#C9A867";

const TOPICS = [
  { value: "", label: "Select a topic..." },
  { value: "Deposits & Withdrawals", label: "Deposits & Withdrawals" },
  { value: "Account & Security", label: "Account & Security" },
  { value: "Report Suspicious Activity", label: "Report Suspicious Activity" },
  { value: "Trading & Orders", label: "Trading & Orders" },
  { value: "Bug Report", label: "Bug Report" },
  { value: "Feature Request", label: "Feature Request" },
  { value: "Other", label: "Other" },
];

export const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const canSubmit =
    name.trim() &&
    email.trim() &&
    topic &&
    message.trim() &&
    status !== "sending";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "contact",
          name: name.trim(),
          email: email.trim(),
          subject: topic || undefined,
          message: message.trim(),
        }),
      });

      const data = await res.json();

      if (data.success) {
        setStatus("success");
        setName("");
        setEmail("");
        setTopic("");
        setMessage("");
      } else {
        setStatus("error");
        setErrorMsg(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <SupportLayout>
      {/* ── Intro Section ── */}
      <section className="mb-10">
        <h1 className="text-3xl font-bold mb-3 text-gray-900 dark:text-gray-100">
          Need Help?
        </h1>
        <p className="text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl">
          Most questions are already answered in our documentation — checking
          there first is usually the fastest way to find what you're looking for.
        </p>
        <p className="text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl mt-3">
          We don't offer live chat — our team is focused on building the best
          possible platform for you. But when you need help, we're here. Send us
          a message and we'll get back to you within{" "}
          <strong className="text-gray-700 dark:text-gray-300">
            24–48 hours
          </strong>
          .
        </p>
        <p className="text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl mt-3">
          If it's about{" "}
          <strong className="text-gray-700 dark:text-gray-300">
            deposits, withdrawals, account security, or suspicious activity
          </strong>
          , please don't hesitate to reach out — we want to hear from you.
        </p>
      </section>

      {/* ── Resource Cards ── */}
      <section className="mb-10">
        <div className="grid gap-4 sm:grid-cols-2">
          <a
            href={DOCS_URL}
            className="group flex items-start gap-3 p-5 bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md hover:border-gray-200 dark:hover:border-gray-700 transition-all"
          >
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 mt-0.5"
              style={{ backgroundColor: `${GOLD}15` }}
            >
              <BookOpen
                className="h-4 w-4"
                style={{ color: GOLD }}
              />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                Documentation
                <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-1">
                Find guides, tutorials, and answers to common questions
              </p>
            </div>
          </a>

          <a
            href={STATUS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-3 p-5 bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md hover:border-gray-200 dark:hover:border-gray-700 transition-all"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/20 flex-shrink-0 mt-0.5">
              <Activity className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                System Status
                <ExternalLink className="h-3 w-3 text-gray-400 group-hover:text-gray-500 transition-colors" />
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-1">
                Check platform uptime, incidents, and maintenance updates
              </p>
            </div>
          </a>
        </div>
      </section>

      {/* ── Contact Form Card ── */}
      <section className="mb-10">
        <div className="bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 sm:px-8 py-5 sm:py-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Send us a message
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Fill out the form below and we'll get back to you as soon as we can.
          </p>

          {status === "success" ? (
            <div className="py-8 text-center">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                style={{ backgroundColor: `${GOLD}15` }}
              >
                <CheckCircle className="h-7 w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Message Sent!
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                Thank you for reaching out. We'll review your message and
                respond within 24–48 hours.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="text-sm font-medium hover:underline cursor-pointer"
                style={{ color: GOLD }}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name + Email row */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="contact-name"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="contact-name"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="contact-email"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all"
                  />
                </div>
              </div>

              {/* Topic dropdown */}
              <div className="space-y-1.5">
                <label
                  htmlFor="contact-topic"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Topic <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    id="contact-topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    required
                    className={`w-full h-11 px-4 pr-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all cursor-pointer ${
                      topic
                        ? "text-gray-900 dark:text-gray-100"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  >
                    {TOPICS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-1.5">
                <label
                  htmlFor="contact-message"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="contact-message"
                  placeholder="Describe your question or issue in detail..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all resize-none"
                />
              </div>

              {/* Error */}
              {status === "error" && (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {errorMsg}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold text-white h-11 px-8 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 cursor-pointer"
                style={{ background: GOLD }}
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── Security Reminder ── */}
      <section className="mb-12">
        <div className="p-5 bg-gray-50 dark:bg-gray-900/30 rounded-2xl border border-gray-100 dark:border-gray-800">
          <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2">
            Security Reminder
          </h3>
          <ul className="space-y-1.5 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            <li>
              <strong className="text-gray-600 dark:text-gray-300">
                Never share your seed phrase or private keys
              </strong>{" "}
              with anyone, including Nordex staff.
            </li>
            <li>
              <strong className="text-gray-600 dark:text-gray-300">
                Verify the URL
              </strong>{" "}
              before connecting your wallet — official domains are{" "}
              <span className="font-medium text-gray-700 dark:text-gray-200">
                nordex.trade
              </span>{" "}
              and{" "}
              <span className="font-medium text-gray-700 dark:text-gray-200">
                app.nordex.trade
              </span>
              .
            </li>
            <li>
              <strong className="text-gray-600 dark:text-gray-300">
                Be cautious of phishing
              </strong>{" "}
              via email, Discord DMs, or Telegram. Nordex staff will never DM
              you first.
            </li>
          </ul>
        </div>
      </section>
    </SupportLayout>
  );
};
